import React from "react";
import { toast } from "react-toastify";
import { Button } from "../../../ui/components/Button";
import { useAppSelector } from "../../../store/hooks";
import {
  CardDefinition,
  CardInstance,
  EffectLifetime,
  Playability,
  useGetRecentAotdCreatorsQuery,
  useSearchRecipientsQuery,
  usePlayCardMutation,
} from "../api/respectCardsApi";
import RespectCard from "./RespectCard";
import { isAnnouncedCard, isHighImpactCard } from "../lib/utils";
import { resolvePlayError } from "../lib/playErrors";
import "../styles/cards.css";

/** The mute — the one card that names more than one victim. */
const MUTE_EFFECT_TYPE = "suppress_other_aotd_notifications";

/** Formats an ISO/Date moment as a local wall-clock time, e.g. "18:45". */
function formatWallClock(value: string | Date): string | null {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

/** "today" when the moment falls on the local calendar day, else "on <date>". */
function daySuffix(date: Date): string {
  const now = new Date();
  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();
  return sameDay
    ? "today"
    : `on ${date.toLocaleDateString([], { month: "short", day: "numeric" })}`;
}

/**
 * Pre-play lifetime sentence. `fixed` windows are measured from play time, so
 * we can state a concrete wall-clock end ("runs ~2h — until ~18:45 today").
 * Every other kind (instant, anchored-to-scheduled-time, until-event, charges,
 * end-of-day) has no honest from-now end before the tap, so we show the label.
 */
function lifetimeSentence(lifetime: EffectLifetime | null | undefined): string {
  if (!lifetime) return "Duration: instant.";
  if (lifetime.kind === "fixed" && typeof lifetime.seconds === "number") {
    const end = new Date(Date.now() + lifetime.seconds * 1000);
    const clock = formatWallClock(end);
    if (clock) {
      return `Runs for ${lifetime.label} — until ~${clock} ${daySuffix(end)}.`;
    }
  }
  return `Runs: ${lifetime.label}.`;
}

/** Which target-picker pool a card draws from, or null when it needs none. */
function targetPool(
  def: CardDefinition | null | undefined,
  playability: Playability | null | undefined,
): "recent_creators" | "any_user" | null {
  if (playability?.pool) return playability.pool;
  if (!def?.effect || def.effect.target !== "target_user") return null;
  // Fallback when no server playability is supplied (e.g. the AOTD anonymize
  // flow mounts the modal from a raw instance): derive from the effect type.
  return def.effect.type === "transfer_respect" || def.effect.type === MUTE_EFFECT_TYPE
    ? "any_user"
    : "recent_creators";
}

/**
 * How many people this play may name. The server declares it on the tile's
 * playability (`Playability.maxTargets`, `MUTE_MAX_TARGETS` on the backend);
 * the effect-type fallback covers the surfaces that mount the modal without a
 * tile. Absent means one, which is every card but the mute.
 */
function maxSelectionsFor(
  def: CardDefinition | null | undefined,
  playability: Playability | null | undefined,
): number {
  const declared = playability?.maxTargets;
  if (typeof declared === "number" && declared > 0) return declared;
  return def?.effect?.type === MUTE_EFFECT_TYPE ? 3 : 1;
}

/** "1 respect" / "2 respect" — the treasury charge, in words. */
function costSentence(playCost: number): string {
  return playCost === 1
    ? "Costs 1 respect, paid to the treasury the moment it lands."
    : `Costs ${playCost} respect, paid to the treasury the moment it lands.`;
}

/**
 * The site clock the taunt quotes. The backend formats the unlock in
 * `SITE_TIMEZONE` (default Europe/Athens) inside
 * `modules/cards/services/runtime/cardPlayNotifier.ts::formatLocalUnlockLabel`,
 * and nothing exposes it over the wire, so the preview mirrors that default
 * against the daily 04:50 UTC anchor. The real unlock carries a few minutes of
 * per-day calendar offset, which is why the preview is labelled as a preview.
 */
const SITE_TIMEZONE = "Europe/Athens";
const UNLOCK_HOUR_UTC = 4;
const UNLOCK_MINUTE_UTC = 50;

export function previewUnlockLabel(now: Date = new Date()): string {
  const anchor = new Date(now.getTime());
  anchor.setUTCHours(UNLOCK_HOUR_UTC, UNLOCK_MINUTE_UTC, 0, 0);
  const city = (SITE_TIMEZONE.split("/").pop() ?? SITE_TIMEZONE).replace(/_/g, " ");
  try {
    const clock = new Intl.DateTimeFormat("en-GB", {
      timeZone: SITE_TIMEZONE,
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(anchor);
    return `${clock} ${city} time`;
  } catch {
    return `0${UNLOCK_HOUR_UTC}:${UNLOCK_MINUTE_UTC} UTC`;
  }
}

/**
 * What every name on the list reads the instant the card lands. Mirrors
 * `sendMuteTaunts` — the unlock time rides inside it on purpose, so what the
 * victim loses is the alarm and never the information.
 */
export function muteTauntPreview(actorUsername: string | null): string {
  const actor = actorUsername ? `@${actorUsername}` : "Someone";
  return (
    `${actor} muted you for the next AOTD. No reminder is coming. ` +
    `Unlock is at ${previewUnlockLabel()}. Show up anyway and you get paid extra.`
  );
}

interface PlayCardModalProps {
  card: CardInstance | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPlayed?: () => void;
  playContext?: { aotdId?: string };
  /**
   * Server playability for the copy being played (from the tile's
   * `playability`). Drives the pre-flight block/deadline and the target pool.
   * Optional — surfaces that mount the modal without a tile (AOTD anonymize)
   * omit it and fall back to a plain "ready" flow.
   */
  playability?: Playability | null;
}

/** One name on the selection. */
export interface PickedTarget {
  userId: string;
  username: string;
}

interface TargetPickerProps {
  picked: PickedTarget[];
  /** 1 for every card but the mute, which names up to three. */
  maxSelections: number;
  onToggle: (target: PickedTarget) => void;
}

/**
 * Adds, removes or replaces a name. At `maxSelections` 1 a tap replaces the
 * pick (the old single-select behaviour); above it the picker is a real
 * multi-select that stops accepting names once it is full, so the client never
 * posts a selection the server would reject with `too_many_targets`.
 */
export function toggleTarget(
  picked: PickedTarget[],
  target: PickedTarget,
  maxSelections: number,
): PickedTarget[] {
  const already = picked.some((p) => p.userId === target.userId);
  if (already) return picked.filter((p) => p.userId !== target.userId);
  if (maxSelections <= 1) return [target];
  if (picked.length >= maxSelections) return picked;
  return [...picked, target];
}

/** "Pick up to 3 players to mute" / the single-pick label. */
function pickLabel(single: string, maxSelections: number, picked: number): string {
  if (maxSelections <= 1) return single;
  return `Pick up to ${maxSelections} players — ${picked} chosen:`;
}

/** Shared button state so both pools behave the same at the cap. */
function pickButtonProps(
  target: PickedTarget,
  picked: PickedTarget[],
  maxSelections: number,
) {
  const isPicked = picked.some((p) => p.userId === target.userId);
  const atCap = !isPicked && maxSelections > 1 && picked.length >= maxSelections;
  return {
    isPicked,
    atCap,
    className: `respect-modal__pick${isPicked ? " is-picked" : ""}${
      atCap ? " is-capped" : ""
    }`,
  };
}

function RecentCreatorsTargetPicker({
  picked,
  maxSelections,
  onToggle,
}: TargetPickerProps) {
  const { data, isLoading, isError } = useGetRecentAotdCreatorsQuery({
    limit: 5,
  });

  if (isLoading) {
    return <p className="respect-modal__copy">Loading recent creators…</p>;
  }
  if (isError || !data) {
    return (
      <p className="respect-modal__copy respect-modal__copy--error">
        Couldn&apos;t load eligible targets. Try again in a moment.
      </p>
    );
  }
  if (data.creators.length === 0) {
    return (
      <p className="respect-modal__copy">
        No eligible targets right now — no recent AOTD creators to lock out.
      </p>
    );
  }

  return (
    <div>
      <p className="respect-modal__copy respect-modal__pick-label">
        {pickLabel(
          "Pick one of the last 5 AOTD creators to block:",
          maxSelections,
          picked.length,
        )}
      </p>
      <div className="respect-modal__pick-row">
        {data.creators.map((c) => {
          const target = { userId: c.userId, username: c.username };
          const { isPicked, atCap, className } = pickButtonProps(
            target,
            picked,
            maxSelections,
          );
          return (
            <button
              key={c.userId}
              type="button"
              aria-pressed={isPicked}
              disabled={atCap}
              onClick={() => onToggle(target)}
              className={className}
            >
              @{c.username}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Recipient picker for Tribute (transfer_respect) and Radio Silence: search any
// other player by username and pick them. Backed by the player-facing
// recipient-search endpoint, which already excludes the requester (no self-gift,
// and no muting yourself either).
function RespectGiftTargetPicker({
  picked,
  maxSelections,
  onToggle,
}: TargetPickerProps) {
  const [term, setTerm] = React.useState("");
  const [debounced, setDebounced] = React.useState("");

  React.useEffect(() => {
    const id = window.setTimeout(() => setDebounced(term.trim()), 250);
    return () => window.clearTimeout(id);
  }, [term]);

  const { data, isFetching, isError } = useSearchRecipientsQuery({
    query: debounced,
    limit: 10,
  });

  const recipients = data?.users ?? [];

  return (
    <div>
      <p className="respect-modal__copy respect-modal__pick-label">
        {pickLabel(
          "Search for the player to send respect to:",
          maxSelections,
          picked.length,
        )}
      </p>
      {maxSelections > 1 && picked.length > 0 ? (
        <div className="respect-modal__picked-row">
          {picked.map((p) => (
            <button
              key={p.userId}
              type="button"
              className="respect-modal__picked"
              onClick={() => onToggle(p)}
              aria-label={`Remove @${p.username} from the list`}
            >
              @{p.username} ✕
            </button>
          ))}
        </div>
      ) : null}
      <input
        type="text"
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        placeholder="Type a username…"
        aria-label="Search recipient by username"
        className="respect-modal__search"
      />
      {isError ? (
        <p className="respect-modal__copy respect-modal__copy--error">
          Couldn&apos;t search players. Try again in a moment.
        </p>
      ) : null}
      {!isError && isFetching ? (
        <p className="respect-modal__copy">Searching…</p>
      ) : null}
      {!isError && !isFetching && recipients.length === 0 ? (
        <p className="respect-modal__copy">
          {debounced ? "No players match that name." : "Start typing to find a player."}
        </p>
      ) : null}
      <div className="respect-modal__pick-row">
        {recipients.map((u) => {
          const target = { userId: u.userId, username: u.username };
          const { isPicked, atCap, className } = pickButtonProps(
            target,
            picked,
            maxSelections,
          );
          return (
            <button
              key={u.userId}
              type="button"
              aria-pressed={isPicked}
              disabled={atCap}
              onClick={() => onToggle(target)}
              className={className}
            >
              {u.userEmoji ? `${u.userEmoji} ` : ""}@{u.username}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function PlayCardModal({
  card,
  open,
  onOpenChange,
  onPlayed,
  playContext,
  playability,
}: PlayCardModalProps) {
  const [playCard, { isLoading }] = usePlayCardMutation();
  const [highImpactConfirmed, setHighImpactConfirmed] = React.useState(false);
  const [picked, setPicked] = React.useState<PickedTarget[]>([]);
  const viewerUsername = useAppSelector((state) => state.auth.username);

  const titleId = React.useId();

  React.useEffect(() => {
    if (!open) {
      setHighImpactConfirmed(false);
      setPicked([]);
    }
  }, [open, card?._id]);

  React.useEffect(() => {
    if (!open) return undefined;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onOpenChange(false);
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [onOpenChange, open]);

  if (!open || !card) return null;

  const def = card.definition;
  const highImpact = isHighImpactCard(def);
  const needsHighImpactConfirm = highImpact && !highImpactConfirmed;

  const pool = targetPool(def, playability);
  const requiresTarget = pool !== null;
  const maxSelections = maxSelectionsFor(def, playability);
  const targetReady = !requiresTarget || picked.length > 0;
  const isMute = def?.effect?.type === MUTE_EFFECT_TYPE;
  const playCost = def?.playCost ?? 0;

  // Pre-flight from server playability. `blocked` disables the CTA with the
  // human reason; `play_before` shows the deadline; anything else is playable.
  const isBlocked = playability?.state === "blocked";
  const blockReason = isBlocked
    ? resolvePlayError({ data: { message: playability?.code, errorCode: playability?.code } }).hint
    : null;
  const playBeforeAt =
    playability?.state === "play_before" && playability.deadline
      ? formatWallClock(playability.deadline)
      : null;

  const cardName = def?.name || "this card";

  const handleActivate = async () => {
    try {
      // Multi-target cards post the whole selection; single-target cards keep
      // the `targetUserId` shape every other handler reads.
      const targetBody =
        !requiresTarget || picked.length === 0
          ? {}
          : maxSelections > 1
            ? {
                targetUserIds: picked.map((p) => p.userId),
                targetUsernames: picked.map((p) => p.username),
              }
            : {
                targetUserId: picked[0].userId,
                targetUsername: picked[0].username,
              };

      const response = await playCard({
        cardInstanceId: card._id,
        ...(playContext?.aotdId ? { aotdId: playContext.aotdId } : {}),
        ...targetBody,
      }).unwrap();

      const activeUntil = response.effectExpiresAt
        ? formatWallClock(response.effectExpiresAt)
        : null;
      // Cards on the audience list are announced to the room by name, so the
      // toast says so — the play being public is the point of those cards.
      const successCopy = isAnnouncedCard(def)
        ? activeUntil
          ? `Played. Everyone was told — active until ${activeUntil}.`
          : "Played. Everyone was told."
        : activeUntil
          ? `Played — active until ${activeUntil}.`
          : response.message ||
            (def?.effectLifetime
              ? `Played — ${def.effectLifetime.label}.`
              : "Card played.");

      toast.success(successCopy, { position: "bottom-right" });
      onOpenChange(false);
      onPlayed?.();
    } catch (playError) {
      const { title, hint, staleInstance } = resolvePlayError(playError);
      toast.error(`${title} — ${hint}`, { position: "bottom-right" });
      if (staleInstance) {
        // The copy is gone from the hand; close and let the hand refetch.
        onOpenChange(false);
        onPlayed?.();
      }
    }
  };

  return (
    <div
      className="respect-modal"
      role="presentation"
      onClick={() => onOpenChange(false)}
    >
      <div
        className="respect-modal__content"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="respect-modal__grid">
          {def ? <RespectCard card={def} size="lg" /> : null}

          <div>
            <h3 className="respect-modal__title" id={titleId}>
              Use {cardName}?
            </h3>
            <p className="respect-modal__copy">
              {def
                ? `This uses the card from your hand and starts its effect: ${def.description}`
                : "This card will leave your hand and its effect will be recorded on your account."}{" "}
              {lifetimeSentence(def?.effectLifetime)}
            </p>

            {playCost > 0 ? (
              <p className="respect-modal__cost">
                <span aria-hidden="true">💠</span> {costSentence(playCost)}
              </p>
            ) : null}

            {isBlocked ? (
              <p className="respect-modal__block-callout" role="alert">
                🚫 Can&apos;t play now — {blockReason}
              </p>
            ) : playBeforeAt ? (
              <p className="respect-modal__time-callout" role="note">
                ⏰ Play before ~{playBeforeAt} today — after that this card
                can&apos;t be used until the next drop.
              </p>
            ) : null}

            {requiresTarget ? (
              pool === "any_user" ? (
                <RespectGiftTargetPicker
                  picked={picked}
                  maxSelections={maxSelections}
                  onToggle={(t) =>
                    setPicked((prev) => toggleTarget(prev, t, maxSelections))
                  }
                />
              ) : (
                <RecentCreatorsTargetPicker
                  picked={picked}
                  maxSelections={maxSelections}
                  onToggle={(t) =>
                    setPicked((prev) => toggleTarget(prev, t, maxSelections))
                  }
                />
              )
            ) : null}

            {isMute ? (
              <div className="respect-modal__taunt">
                <p className="respect-modal__taunt-label">
                  Every name you pick reads this the moment you play — the
                  unlock time is in it, so you take their alarm, never the
                  information:
                </p>
                <p className="respect-modal__taunt-quote">
                  {muteTauntPreview(viewerUsername)}
                </p>
              </div>
            ) : null}

            {needsHighImpactConfirm ? (
              <>
                <p className="respect-modal__copy respect-modal__copy--warn">
                  This card has high impact. Are you sure?
                </p>
                <div className="respect-modal__actions">
                  <Button
                    onClick={() => setHighImpactConfirmed(true)}
                    variant="secondary"
                    disabled={!targetReady || isBlocked}
                    title={isBlocked ? blockReason ?? undefined : undefined}
                  >
                    Yes, use it
                  </Button>
                  <Button onClick={() => onOpenChange(false)} variant="outline">
                    No, cancel
                  </Button>
                </div>
              </>
            ) : (
              <div className="respect-modal__actions">
                <Button
                  onClick={handleActivate}
                  loading={isLoading}
                  variant="secondary"
                  disabled={!targetReady || isBlocked}
                  title={isBlocked ? blockReason ?? undefined : undefined}
                >
                  {isBlocked
                    ? "Can't play now"
                    : requiresTarget && !targetReady
                      ? maxSelections > 1
                        ? "Pick at least one name"
                        : "Pick a target first"
                      : "Use card"}
                </Button>
                <Button onClick={() => onOpenChange(false)} variant="outline">
                  Cancel
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
